import type { OptimizationSuggestion } from "@/client/pages/dashboard/ai-optimization/review";

/**
 * Which resume section a suggestion belongs to, in the builder's own section-id space.
 *
 * Derived from `fieldPath` in preference to the model-supplied `sectionId`, because fieldPath is
 * the authoritative write target — it is what `setValue` uses — whereas `sectionId` is free text
 * from the LLM and falls back to the literal "resume" when absent (see
 * `parseOptimizationSuggestions`). Now that each suggestion is rendered inside its own section
 * rather than in one shared panel, an unrecognised sectionId would mean the card renders NOWHERE,
 * so the more reliable source has to win.
 *
 * Returns ids that match `SectionKey`: "summary", "experience", …, and `custom.<id>`.
 */
export const resolveSuggestionSectionId = (suggestion: OptimizationSuggestion): string => {
  const custom = /^sections\.custom\.([^.[]+)/.exec(suggestion.fieldPath);
  if (custom?.[1]) return `custom.${custom[1]}`;

  const standard = /^sections\.([^.[]+)/.exec(suggestion.fieldPath);
  if (standard?.[1] && standard[1] !== "custom") return standard[1];

  return suggestion.sectionId;
};

export const filterSuggestionsBySection = (
  suggestions: OptimizationSuggestion[],
  sectionId: string,
) => suggestions.filter((suggestion) => resolveSuggestionSectionId(suggestion) === sectionId);

/**
 * Skill additions have no sectionId of their own — they are always appended to
 * `sections.skills.items`, so the skills section is where they belong.
 */
export const COACH_SKILL_ADDITIONS_SECTION_ID = "skills";
